import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogClose } from "@/components/ui/dialog";
import { FaWhatsapp } from "react-icons/fa";
import { Music2, Wine, Heart, X, ChevronRight, Home as HomeIcon, Sparkles, Church, Flower2 } from "lucide-react";
import hotelsImg from "@/assets/card-hotel-events-crop.jpg.asset.json";
import residentialImg from "@/assets/card-residential-new.jpg.asset.json";
import weddingImg from "@/assets/card-wedding.jpg.asset.json";
import funeralImg from "@/assets/card-funeral.jpg.asset.json";
import { WHATSAPP_URL } from "@/lib/whatsapp";

export type Occasion = "hotels" | "residential" | "weddings" | "funerals";

type Content = {
  eyebrow: string;
  title: string;
  intro: string;
  image: string;
  imageAlt: string;
  pointsLabel: string;
  points: { icon: typeof Music2; text: string }[];
  message: string;
};

const CONTENT: Record<Occasion, Content> = {
  hotels: {
    eyebrow: "Live vocals",
    title: "Hotels & Events",
    intro: "Live vocals for hotel lounges, elegant events and atmospheric evenings.",
    image: hotelsImg.url,
    imageAlt: "Red velvet curtain with a hanging vintage microphone in a dark lounge",
    pointsLabel: "Performance options",
    points: [
      { icon: Music2, text: "45 minute live performance" },
      { icon: Wine, text: "Tailored to hotel lounges, bars, private events and special occasions" },
      { icon: Music2, text: "Classical and contemporary music to complement each venue" },
      { icon: Heart, text: "Please get in touch for pricing and availability" },
    ],
    message:
      "Hi Olivia, I'm interested in booking a live performance for our venue. Could you please send me some more information about pricing and availability?",
  },
  residential: {
    eyebrow: "Live vocals",
    title: "Residential Homes & Retirement Villages",
    intro: "Relaxed, uplifting performances tailored to your residents, with a style to suit every room.",
    image: residentialImg.url,
    imageAlt: "Cosy residential lounge with a grand piano and afternoon tea",
    pointsLabel: "Performance options",
    points: [
      { icon: HomeIcon, text: "45 minute live performance" },
      { icon: Sparkles, text: "A warm mix of familiar classics and well loved songs residents can sing along to" },
      { icon: HomeIcon, text: "Gentle pacing, comfortable volume and a setup that fits your lounge" },
      { icon: Heart, text: "Please get in touch for pricing and availability" },
    ],
    message:
      "Hi Olivia, I'm interested in booking a live performance for our residents. Could you please send me some more information about pricing and availability?",
  },
  weddings: {
    eyebrow: "Live vocals",
    title: "Weddings",
    intro: "Timeless songs for your ceremony and your day, sung with elegance and warmth.",
    image: weddingImg.url,
    imageAlt: "Vintage silver microphones decorated with red roses at a wedding ceremony",
    pointsLabel: "Performance options",
    points: [
      { icon: Church, text: "Music for ceremonies, drinks receptions and wedding breakfasts" },
      { icon: Sparkles, text: "Classical and contemporary songs chosen with you, for your day" },
      { icon: Church, text: "A solo vocalist setup that fits quietly into your venue" },
      { icon: Heart, text: "Please get in touch for pricing and availability" },
    ],
    message:
      "Hi Olivia, I'm interested in live music for our wedding. Could you please send me some more information about pricing and availability?",
  },
  funerals: {
    eyebrow: "Live vocals",
    title: "Funerals",
    intro: "Gentle, heartfelt music to say goodbye, handled with care and sensitivity.",
    image: funeralImg.url,
    imageAlt: "Tall arched stained glass window in a cathedral",
    pointsLabel: "Performance options",
    points: [
      { icon: Flower2, text: "Music for services, including hymns and personal favourites" },
      { icon: Heart, text: "Sung with warmth, at a gentle volume, wherever the service is held" },
      { icon: Flower2, text: "Happy to talk through the songs that would mean the most" },
      { icon: Heart, text: "Please get in touch for pricing and availability" },
    ],
    message:
      "Hi Olivia, I'm interested in live music for a funeral service. Could you please send me some more information about pricing and availability?",
  },
};

export default function OccasionDialog({ occasion, onClose }: { occasion: Occasion | null; onClose: () => void }) {
  const c = occasion ? CONTENT[occasion] : null;
  return (
    <Dialog open={!!occasion} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl w-[92vw] p-0 gap-0 overflow-hidden bg-popup border border-gold/50 rounded-2xl max-h-[90vh] overflow-y-auto [&>button:last-child]:hidden">
        {c && (
          <div className="grid md:grid-cols-[1fr_1.15fr]">
            <div className="p-4 md:p-5">
              <img src={c.image} alt={c.imageAlt} className="w-full h-56 md:h-full object-cover object-top md:object-center rounded-lg" />
            </div>
            <div className="p-6 md:p-8 md:pl-3 flex flex-col">
              <p className="text-gold text-[10px] tracking-[0.5em] uppercase mb-3">{c.eyebrow}</p>
              <DialogTitle className="font-playfair text-3xl md:text-4xl text-foreground font-normal">{c.title}</DialogTitle>
              <div className="flex items-center gap-2 my-4 text-gold">
                <span className="h-px w-16 bg-gold/60" />✦<span className="h-px w-16 bg-gold/60" />
              </div>
              <DialogDescription className="font-playfair text-base md:text-lg text-foreground/85 leading-relaxed">
                {c.intro}
              </DialogDescription>
              <p className="text-gold text-[10px] tracking-[0.4em] uppercase mt-6 mb-3">{c.pointsLabel}</p>
              <ul className="space-y-3">
                {c.points.map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-center gap-3 text-sm text-foreground/80">
                    <span className="shrink-0 w-9 h-9 rounded-full border border-gold/60 flex items-center justify-center text-gold">
                      <Icon className="w-4 h-4" />
                    </span>
                    {text}
                  </li>
                ))}
              </ul>
              <a
                href={`${WHATSAPP_URL}?text=${encodeURIComponent(c.message)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 inline-flex items-center justify-center gap-3 rounded-full border border-gold/70 bg-whatsapp text-foreground px-6 py-3.5 font-playfair text-base shadow-lg hover:brightness-110 transition"
              >
                <FaWhatsapp className="w-5 h-5" /> Message on WhatsApp <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        )}
        <DialogClose
          className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-gold/60 bg-popup/80 text-gold shadow-lg shadow-black/50 backdrop-blur-sm transition-colors hover:bg-popup hover:text-foreground"
          aria-label="Close pop up"
        >
          <X className="h-5 w-5" />
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}
